import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import {
  X,
  Loader2,
  Download,
  ArrowLeft,
  ArrowRight,
  Layers,
  Grid,
  RotateCcw,
} from "lucide-react";

interface ProgressPhoto {
  id: string;
  storage_path: string;
  photo_type: string;
  photo_date: string;
  caption: string | null;
}

interface PhotoOverlayViewProps {
  photos: ProgressPhoto[];
  onClose?: () => void;
}

const photoTypeLabels: Record<string, string> = {
  front: "Front",
  side: "Side",
  back: "Back",
  other: "Other",
};

export default function PhotoOverlayView({ photos, onClose }: PhotoOverlayViewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [beforeIndex, setBeforeIndex] = useState(0);
  const [afterIndex, setAfterIndex] = useState(Math.min(1, photos.length - 1));
  const [beforeUrl, setBeforeUrl] = useState<string | null>(null);
  const [afterUrl, setAfterUrl] = useState<string | null>(null);
  const [beforeImage, setBeforeImage] = useState<HTMLImageElement | null>(null);
  const [afterImage, setAfterImage] = useState<HTMLImageElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [opacity, setOpacity] = useState(0.5);
  const [showGuides, setShowGuides] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadImageUrl = async (storagePath: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from("progress-photos")
        .createSignedUrl(storagePath, 3600);

      if (error) throw error;
      return data.signedUrl;
    } catch (err) {
      console.error("Error loading image:", err);
      return null;
    }
  };

  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  // Load images when indices change
  useEffect(() => {
    const loadBothImages = async () => {
      setLoading(true);
      try {
        const [bUrl, aUrl] = await Promise.all([
          loadImageUrl(photos[beforeIndex].storage_path),
          loadImageUrl(photos[afterIndex].storage_path),
        ]);

        setBeforeUrl(bUrl);
        setAfterUrl(aUrl);

        if (bUrl && aUrl) {
          const [bImg, aImg] = await Promise.all([
            loadImage(bUrl),
            loadImage(aUrl),
          ]);
          setBeforeImage(bImg);
          setAfterImage(aImg);
        }
      } catch (err) {
        console.error("Error loading images:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBothImages();
  }, [beforeIndex, afterIndex, photos]);

  // Draw canvas whenever images or opacity changes
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !beforeImage || !afterImage) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size based on the larger image dimensions
    const maxWidth = Math.max(beforeImage.width, afterImage.width);
    const maxHeight = Math.max(beforeImage.height, afterImage.height);

    // Scale down for display while maintaining aspect ratio
    const displayWidth = 400;
    const scale = displayWidth / maxWidth;
    const displayHeight = maxHeight * scale;

    canvas.width = displayWidth;
    canvas.height = displayHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw before image (full opacity)
    ctx.globalAlpha = 1;
    const beforeScale = displayWidth / beforeImage.width;
    ctx.drawImage(
      beforeImage,
      0,
      0,
      displayWidth,
      beforeImage.height * beforeScale
    );

    // Draw after image with opacity overlay
    ctx.globalAlpha = opacity;
    const afterScale = displayWidth / afterImage.width;
    ctx.drawImage(afterImage, 0, 0, displayWidth, afterImage.height * afterScale);

    // Reset alpha
    ctx.globalAlpha = 1;

    // Draw alignment guides if enabled
    if (showGuides) {
      ctx.strokeStyle = "rgba(255, 0, 0, 0.7)";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);

      // Vertical center line
      ctx.beginPath();
      ctx.moveTo(canvas.width / 2, 0);
      ctx.lineTo(canvas.width / 2, canvas.height);
      ctx.stroke();

      // Horizontal lines at 1/3 and 2/3
      ctx.beginPath();
      ctx.moveTo(0, canvas.height / 3);
      ctx.lineTo(canvas.width, canvas.height / 3);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, (canvas.height * 2) / 3);
      ctx.lineTo(canvas.width, (canvas.height * 2) / 3);
      ctx.stroke();

      // Horizontal center line
      ctx.beginPath();
      ctx.moveTo(0, canvas.height / 2);
      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();

      ctx.setLineDash([]);
    }
  }, [beforeImage, afterImage, opacity, showGuides]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const handleExport = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setExporting(true);
    try {
      // Create a higher resolution version for export
      const exportCanvas = document.createElement("canvas");
      const exportCtx = exportCanvas.getContext("2d");
      if (!exportCtx || !beforeImage || !afterImage) return;

      // Use original resolution for export
      const maxWidth = Math.max(beforeImage.width, afterImage.width);
      const maxHeight = Math.max(beforeImage.height, afterImage.height);

      exportCanvas.width = maxWidth;
      exportCanvas.height = maxHeight;

      // Draw at full resolution
      exportCtx.globalAlpha = 1;
      exportCtx.drawImage(beforeImage, 0, 0, maxWidth, beforeImage.height * (maxWidth / beforeImage.width));

      exportCtx.globalAlpha = opacity;
      exportCtx.drawImage(afterImage, 0, 0, maxWidth, afterImage.height * (maxWidth / afterImage.width));

      // Convert to blob and download
      exportCanvas.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const beforeDate = format(new Date(photos[beforeIndex].photo_date), "yyyy-MM-dd");
        const afterDate = format(new Date(photos[afterIndex].photo_date), "yyyy-MM-dd");
        link.download = `progress-overlay-${beforeDate}-to-${afterDate}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }, "image/png");
    } catch (err) {
      console.error("Error exporting image:", err);
    } finally {
      setExporting(false);
    }
  };

  const beforePhoto = photos[beforeIndex];
  const afterPhoto = photos[afterIndex];

  if (photos.length < 2) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Need at least 2 photos for overlay comparison
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Overlay Comparison</CardTitle>
          </div>
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Canvas container */}
        <div className="relative aspect-[3/4] bg-muted rounded-lg overflow-hidden flex items-center justify-center">
          {loading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading images...</span>
            </div>
          ) : (
            <canvas
              ref={canvasRef}
              className="max-w-full max-h-full object-contain"
            />
          )}
        </div>

        {/* Photo info badges */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-info/10 text-info">
              Before: {format(new Date(beforePhoto.photo_date), "MMM d, yyyy")}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              disabled={beforeIndex === 0}
              onClick={() => setBeforeIndex((i) => Math.max(0, i - 1))}
            >
              <ArrowLeft className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              disabled={beforeIndex >= afterIndex - 1}
              onClick={() => setBeforeIndex((i) => Math.min(afterIndex - 1, i + 1))}
            >
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              disabled={afterIndex <= beforeIndex + 1}
              onClick={() => setAfterIndex((i) => Math.max(beforeIndex + 1, i - 1))}
            >
              <ArrowLeft className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              disabled={afterIndex === photos.length - 1}
              onClick={() => setAfterIndex((i) => Math.min(photos.length - 1, i + 1))}
            >
              <ArrowRight className="h-3 w-3" />
            </Button>
            <Badge variant="secondary" className="bg-success/10 text-success">
              After: {format(new Date(afterPhoto.photo_date), "MMM d, yyyy")}
            </Badge>
          </div>
        </div>

        {/* Opacity slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Overlay Opacity</Label>
            <span className="text-sm text-muted-foreground">
              {Math.round(opacity * 100)}%
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Before</span>
            <Slider
              value={[opacity]}
              onValueChange={([value]) => setOpacity(value)}
              min={0}
              max={1}
              step={0.05}
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground">After</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setOpacity(0.5)}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset to 50%
          </Button>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-2">
            <Switch
              id="show-guides"
              checked={showGuides}
              onCheckedChange={setShowGuides}
            />
            <Label htmlFor="show-guides" className="flex items-center gap-1 text-sm cursor-pointer">
              <Grid className="h-4 w-4" />
              Alignment Guides
            </Label>
          </div>
          <Button onClick={handleExport} disabled={loading || exporting}>
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export Image
              </>
            )}
          </Button>
        </div>

        {/* Photo type indicator */}
        <div className="text-center text-xs text-muted-foreground">
          Comparing {photoTypeLabels[beforePhoto.photo_type] || beforePhoto.photo_type} photos
        </div>
      </CardContent>
    </Card>
  );
}
